import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import BaseCard from "../../components/ui/BaseCard";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";

import {
  fetchAllSubCategories,
  fetchCategories,
  fetchAllVendors,
} from "../../services/api/category";

import AddEditProductModal from "./modals/AddEditProductModal";
import { debounce } from "../../utils/helpers";

const menuOptions = [];

export default function ProductsTable() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [isProdcutModalOpen, setProdcutModalOpen] = useState(false);
  const [prodMode, setProdMode] = useState("add");
  const [prodInitial, setProdInitial] = useState(null);
  const [rowCountState, setRowCountState] = useState(0);
  const params = useMemo(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchQuery || undefined,
    }),
    [paginationModel, searchQuery]
  );

  const {
    data: subCategoriesData,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["subCategories", params],
    queryFn: () => fetchAllSubCategories(params),
    keepPreviousData: true,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: fetchAllVendors,
  });

  const categories = useMemo(
    () => categoriesData?.data ?? [],
    [categoriesData]
  );
  const vendors = useMemo(() => vendorsData?.data ?? [], [vendorsData]);

  useEffect(() => {
    setRowCountState((prev) => subCategoriesData?.total ?? prev);
  }, [subCategoriesData?.total]);

  const rows = useMemo(() => {
    const list = subCategoriesData?.data ?? [];
    return list.map((sc) => ({
      id: sc.id,
      product: sc.subCategory,
      vendor: sc.vendor?.name ?? "-",
      vendorId: sc.vendor?.id ?? sc.vendorId ?? null,
      categoryName: sc.vendor?.category?.category ?? "-",
      categoryId: sc.vendor?.category?.id ?? sc.categoryId ?? null,
      createdAt: sc.createdAt,
    }));
  }, [subCategoriesData]);

  const openAdd = () => {
    setProdMode("add");
    setProdInitial(null);
    setProdcutModalOpen(true);
  };

  const openEdit = (row) => {
    setProdMode("edit");
    setProdInitial({
      id: row.id,
      categoryId: row.categoryId,
      vendorId: row.vendorId,
      productNames: row.product,
    });
    setProdcutModalOpen(true);
  };

  const handleMenuClick = (item, row) => {
    if (item.value === "EDIT") openEdit(row);
    if (item.value === "DELETE") {
    }
  };
  const debouncedSetSearchQuery = useRef(
    debounce((val) => {
      setSearchQuery(val);
      setPaginationModel((m) => ({ ...m, page: 0 }));
    }, 400)
  );

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSetSearchQuery.current(val);
  };

  const columns = [
    {
      field: "product",
      headerName: "Skill",
      flex: 1,
      renderCell: (p) => <button className="text-primary ">{p.value}</button>,
    },
    { field: "vendor", headerName: "Category", flex: 1 },
    { field: "categoryName", headerName: "Technology Stack", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />
          }
          onItemClick={(item) => handleMenuClick(item, params.row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
        <div className="flex justify-between items-center w-full gap-4">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
            <p className="text-primary ml-2">Add Filter</p>
          </div>

          <div className="w-[76%]">
            <BaseTextField
              placeholder="Search skill, category, technology stack"
              value={searchInput}
              onChange={handleSearch}
              startAdornment={
                <i className="fa-light fa-magnifying-glass text-lg" />
              }
            />
          </div>

          <BaseButton
            className="md:w-auto w-full"
            variant="contained"
            color="primary"
            style={{ padding: "6px 12px" }}
            onClick={openAdd}
          >
            Create New Skill
          </BaseButton>
        </div>
      </div>

      <BaseCard
        className="rounded-tl-xl rounded-tr-xl shadow-lg mt-5"
        elevation={0}
      >
            <BaseDataTableV2
              className="max-h-[calc(100vh-320px)] overflow-auto"
              columns={columns}
              data={rows}
              checkboxSelection
              isLoading={isPending}
              hideFooter={false}
              pagination
              paginationMode="server"
              rowCount={rowCountState}
              pageSizeOptions={[10, 20, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
      </BaseCard>

      {isProdcutModalOpen && (
        <AddEditProductModal
          open={isProdcutModalOpen}
          mode={prodMode}
          initialData={prodInitial}
          categories={categories}
          vendors={vendors}
          close={() => setProdcutModalOpen(false)}
          refetchProducts={refetch}
        />
      )}
    </>
  );
}
